import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { formatCurrency } from "../../../utils/function";
import { DealBoardItem, DealStage, formatSource, formatMoneyCompact } from "./types";
import StageMenu from "./stage-menu";

const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const InfoRow: React.FC<{ label: string; children: React.ReactNode; title?: string }> = ({ label, children, title }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-gray-400 dark:text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-700 dark:text-gray-200 truncate min-w-0 text-right" title={title}>
      {children}
    </span>
  </div>
);

const DealCard: React.FC<{
  deal: DealBoardItem;
  // The dashboard response's own value_currency — used only when a deal has
  // no currency of its own; never a hardcoded code.
  valueCurrency?: string;
  onViewDetails: (deal: DealBoardItem) => void;
  onStageChanged: (dealId: string, stage: DealStage) => void;
  // Native HTML5 drag-and-drop attrs (draggable/onDragStart), spread onto
  // the card root — same pattern as ticket/index.tsx's TicketCard
  // dragHandleProps, which is the proven-working reference for this.
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  // The same onDragOver/onDrop the column background carries, spread here
  // too — a card dropped *directly onto another card* (any column with
  // existing deals, rather than the empty-state placeholder) needs its own
  // handlers rather than relying on bubbling through a draggable descendant,
  // which some browsers don't reliably deliver to the ancestor.
  dropHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}> = ({ deal, valueCurrency, onViewDetails, onStageChanged, dragHandleProps, dropHandleProps }) => {
  const [expanded, setExpanded] = useState(true);
  const { person } = deal;
  const currency = deal.currency || valueCurrency;
  const fullAmount = deal.amount != null && currency ? formatCurrency(Number(deal.amount), currency, "symbol") : "";

  return (
    <div
      {...dragHandleProps}
      {...dropHandleProps}
      className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-3.5 shadow-sm cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          {person.avatar_url ? (
            <img src={person.avatar_url} alt={person.display_name} className="w-8 h-8 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-[11px] font-semibold flex items-center justify-center shrink-0">
              {initials(person.display_name)}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={person.display_name}>
              {person.display_name}
            </p>
            {person.phone && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{person.phone}</p>}
          </div>
        </div>
        <div className="shrink-0">
          <StageMenu dealId={deal.id} stage={deal.stage} onChanged={(s) => onStageChanged(deal.id, s)} />
        </div>
      </div>

      {person.source && (
        <span
          className="mt-2 block max-w-full w-fit truncate text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400"
          title={formatSource(person.source)}
        >
          {formatSource(person.source)}
        </span>
      )}

      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="flex items-center justify-between w-full mt-3 text-xs font-semibold text-gray-500 dark:text-gray-400"
      >
        Contact Info
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5 text-xs">
          {person.email && (
            <InfoRow label="Email" title={person.email}>
              {person.email}
            </InfoRow>
          )}
          {person.location && (
            <InfoRow label="Location" title={person.location}>
              {person.location}
            </InfoRow>
          )}
          {deal.amount != null && currency && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-400 dark:text-gray-500 shrink-0">Deal Potential</span>
              <span
                className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 font-medium truncate"
                title={fullAmount}
              >
                {formatMoneyCompact(Number(deal.amount), currency)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-400 dark:text-gray-500 shrink-0">Date Added</span>
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 font-medium whitespace-nowrap">
              {formatDate(deal.created_at)}
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => onViewDetails(deal)}
        className="mt-3 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
      >
        View Deal Details
      </button>
    </div>
  );
};

export default DealCard;
